/** Apple keyboards: Cmd instead of Ctrl, and symbols for modifier keys. */
export const isApple =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

/** Shows a shortcut written as "Ctrl Shift Z" the way this device labels its keys. */
export function shortcutLabel(shortcut: string): string {
  if (!isApple) return shortcut;
  return shortcut
    .replace(/\bCtrl\b/g, '⌘')
    .replace(/\bAlt\b/g, '⌥')
    .replace(/\bShift\b/g, '⇧');
}

/** What the non-letter keys type on a US keyboard, by physical key. */
const US_KEYS: Record<string, string> = {
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Semicolon: ';',
  Quote: "'",
  Backquote: '`',
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backslash: '\\',
};

/**
 * The key a shortcut should see, lowercased: the Latin letter at the same spot
 * when a Hebrew (or other non-Latin) layout is on, so "V", "Ctrl Z" and "/"
 * work from the same keys whatever language is being typed.
 */
export function shortcutKey(e: Pick<KeyboardEvent, 'key' | 'code' | 'shiftKey'>): string {
  const letter = /^Key([A-Z])$/.exec(e.code)?.[1];
  // A letter key that doesn't type a Latin letter: use the letter printed on it.
  if (letter && !/^[a-z]$/i.test(e.key)) return letter.toLowerCase();
  const digit = /^Digit(\d)$/.exec(e.code)?.[1];
  if (e.key.length === 1 && e.key.charCodeAt(0) > 127) {
    if (digit) return digit;
    if (US_KEYS[e.code]) return US_KEYS[e.code] as string;
  }
  // The Hebrew layout types "." on the US "/" key (and "/" on Q, handled above).
  if (e.code === 'Slash' && e.key === '.' && !e.shiftKey) return '/';
  return e.key.toLowerCase();
}
